import { css, keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { calc, calcMulti, LengthValue } from "@animech/ui";
import { ZIndex } from "src/ui/constants";

type DegreeValue = `${number}deg`;

export const ColorWheel = styled.ul<{ count: number }>`
	${({ count }) => {
		/*
			Adjust the base angle so that the first option is center-top,
			then calculate the rotation based on the total number of options.
		*/
		const baseAngleAdjustment: DegreeValue = "-90deg";
		return css`
			--wheel-rotation: calc(
				${baseAngleAdjustment} - (${count - 1} * var(--color-sector) / 2)
			);
			transform: rotateZ(var(--wheel-rotation));
		`;
	}}
`;

export const ColorWheelOption = styled.li<{
	index: number;
	selected: boolean;
	disabled: boolean;
	image?: string;
}>((props) => {
	const { disabled, index, selected, image } = props;
	const opacity = disabled ? 0.6 : 1;
	const outlineColor = disabled ? "red" : "white";
	return css`
		/*
			Adjust the distance from the center and calculate the angle for each option
		*/
		transform-origin: calc(var(--color-offset-radius) * -1) center;
		transform: translate(0, -50%) translateX(var(--color-offset-radius))
			rotateZ(calc(${index ?? 0} * var(--color-sector)));
		opacity: ${opacity};

		${disabled &&
		css`
			pointer-events: none;
			:hover: {
				transform: unset;
			}
		`}

		&:hover {
			::after {
				transform: rotateZ(
						calc(
							(${index ?? 0} * var(--color-sector) * -1) - var(--wheel-rotation)
						)
					)
					scale(1.1);
			}
		}

		::after {
			background-image: ${image ? `url(${image})` : "none"};
			border-width: var(--color-border);
			border-style: ${selected ? "solid" : "none"};
			border-color: ${outlineColor};
			box-sizing: border-box;
			transition: all 0.1s ease-in-out;
			transform: rotateZ(
					calc(
						(${index ?? 0} * var(--color-sector) * -1) - var(--wheel-rotation)
					)
				)
				scale(1);
		}
	`;
});

export const MiddleSection = styled.div<{ image?: string }>`
	background-image: ${(props) =>
		props.image ? `url(${props.image})` : "none"};
`;

export const CheckBox = styled.div``;
export const Move = styled.div``;
export const Label = styled.span``;
export const Trash = styled.div``;

export const ColorPicker = styled.div<{
	visible?: boolean;
	position?: { x: number; y: number };
	canDelete?: boolean;
	invertColors?: boolean;
}>`
	${({ theme, position, visible = true, canDelete = true, invertColors }) => {
		// the size of each color swatch inside the color wheel
		const colorSize: LengthValue = "26px";

		// how many degrees each color swatch occupies in the color wheel
		const colorSector: DegreeValue = "30deg";

		// the padding around the color swatches and the color wheel
		const wheelPadding: LengthValue = "5px";

		// the empty margin between the color wheel and the inner section
		const innerMargin: LengthValue = "8px";

		// the size of the inner section
		const innerDiameter: LengthValue = "128px";

		const positionX: LengthValue = position ? `${position.x}px` : "50%";
		const positionY: LengthValue = position ? `${position.y}px` : "50%";
		const colorBorder = "2px";
		const wheelThickness = calcMulti(
			[colorSize, colorBorder, wheelPadding],
			([s, b, p]) => s + b + p * 2,
		);
		const widgetDiameter = calcMulti(
			[innerDiameter, innerMargin, wheelThickness],
			([d, m, t]) => d + (m + t) * 2,
		);
		const innerRadius = calc(innerDiameter, (i) => i / 2);
		const innerWheelRadius = calcMulti(
			[innerRadius, innerMargin],
			([r, m]) => r + m,
		);
		const colorOffsetRadius = calcMulti(
			[innerWheelRadius, wheelPadding],
			([r, p]) => r + p,
		);

		return css`
			--color-border: ${colorBorder};
			--color-sector: ${colorSector};
			--color-offset-radius: ${colorOffsetRadius};

			position: absolute;
			display: grid;
			place-items: center;
			top: ${positionY};
			left: ${positionX};
			width: ${widgetDiameter};
			height: ${widgetDiameter};
			border-radius: 50%;
			user-select: none;
			pointer-events: auto;

			animation-duration: 0.3s;
			animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
			animation-fill-mode: both;
			z-index: ${ZIndex.ColorPicker};

			${visible
				? { animationName: fadeInRotate }
				: { animationName: fadeOutRotate }}

			> * {
				grid-area: 1 / 1;
				width: 100%;
				height: 100%;
			}

			${ColorWheel} {
				display: grid;
				place-items: center;
				list-style: none;
				border-radius: 50%;
				padding: 0;
				margin: 0;
				background: radial-gradient(
					circle,
					rgba(0, 0, 0, 0.6) calc(${innerDiameter} / 2),
					rgba(0, 0, 0, 0) calc(${innerDiameter} / 2),
					rgba(0, 0, 0, 0) calc(${innerDiameter} / 2 + ${innerMargin}),
					rgba(255, 255, 255, 0.2) calc(${innerDiameter} / 2 + ${innerMargin})
				);

				${ColorWheelOption} {
					border-radius: 100%;
					width: ${colorSize};
					height: ${colorSize};
					position: absolute;
					top: 50%;
					left: 50%;
					margin: 0;
					cursor: pointer;

					::after {
						content: "";
						background-repeat: no-repeat;
						background-position: center;
						background-size: cover;
						background-attachment: fixed;
						position: absolute;
						top: 0;
						left: 0;
						width: 100%;
						height: 100%;
						border-radius: 100%;
					}
				}
			}

			${MiddleSection} {
				display: grid;
				place-items: center;
				background-repeat: no-repeat;
				background-position: center;
				background-size: cover;
				background-attachment: fixed;
				border-radius: 50%;
				width: ${innerDiameter};
				height: ${innerDiameter};
				transition: all 0.2s ease-in-out;
				z-index: 1;

				> * {
					grid-area: 1 / 1;
				}

				${Move} {
					background-color: black;
					color: white;
					border-radius: 100%;
					width: ${colorSize};
					height: ${colorSize};
					cursor: grab;
					position: relative;
					display: grid;
					place-items: center;
					transform: scale(1);

					&:hover {
						transform: scale(1.1);
					}

					&:active {
						cursor: grabbing;
					}
				}

				${Label} {
					color: white;
					border-radius: 4px;
					padding: 2px 4px;
					text-transform: uppercase;
					position: relative;
					cursor: default;
					max-width: ${calc(innerDiameter, (d) => d - 10)};
					text-shadow: 0 0 3px black;
					text-align: center;
					overflow: hidden;
					text-overflow: ellipsis;

					${invertColors &&
					css`
						color: black;
						text-shadow: 0 0 3px white;
					`}
				}

				${Trash} {
					background-color: ${theme.colors.palette.red[600]};
					color: white;
					border-radius: 100%;
					width: ${colorSize};
					height: ${colorSize};
					cursor: pointer;
					position: relative;
					display: grid;
					place-items: center;
					transform: translateY(calc(50% + ${colorOffsetRadius})) scale(1);

					&:hover {
						transform: translateY(calc(50% + ${colorOffsetRadius})) scale(1.1);
					}

					${!canDelete &&
					css`
						opacity: 0.7;
						background-color: grey;
						pointer-events: none;
					`}
				}
			}

			${CheckBox} {
				position: absolute;
				top: ${calc(widgetDiameter, (wd) => wd + 5)};
				width: auto;
				height: auto;
				grid-area: unset;
				padding: 10px;
				border-radius: 18px;
				background-color: rgba(255, 255, 255, 0.2);
			}
		`;
	}}
`;

export const fadeInRotate = keyframes`
	from {
		opacity: 0;
		transform: translate(-50%, -50%) scale(0.8) rotate(-90deg);
	}
	to {
		opacity: 1;
		transform: translate(-50%, -50%) scale(1) rotate(0deg);
	}
`;

export const fadeOutRotate = keyframes`
	from {
		opacity: 1;
		transform: translate(-50%, -50%) scale(1) rotate(0deg);
	}
	to {
		opacity: 0;
		transform: translate(-50%, -50%) scale(0.8) rotate(-90deg);
	}
`;
